import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { GradientIcon } from '../gradients/GradientIcon';
import { GradientTypography } from '../gradients/GradientTypography';
import Grid from '@mui/material/Grid';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { ParticipantsWithIcon } from './ParticipantsWithIcon';
import { toKebabCase } from '../../util/string';
// import { Link } from '../Link';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { usePrizeFormat } from 'src/hooks/usePrizeFormat';
import { ImageOverlayed } from '../ImageOverlayed';
import { Required } from 'utility-types';
import { TimeStatus } from './TimeStatus';
import { RegisterButton } from './RegisterButton';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

const SX_ROW_GROUP = {
  mx: 4,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};
export type EventProps = Required<
  Pick<
    Tournament<Date>,
    | 'id'
    | 'imgUrl'
    | 'title'
    | 'gameTitle'
    | 'date'
    | 'checkInTime'
    | 'competitorCount'
    | 'prize'
    | 'sponsors'
    | 'maxTeamSize'
    | 'maxTeamCount'
    | 'phase'
    | 'endDate'
    | 'bracketType'
    | 'readyTime'
  >,
  'imgUrl'
> & {
  noDivider?: boolean;
  isRegistered?: boolean;
  isCheckedIn?: boolean;
};

export const Event = ({
  id,
  imgUrl,
  title,
  date,
  endDate,
  isRegistered,
  isCheckedIn,
  prize,
  sponsors,
  gameTitle,
  competitorCount,
  maxTeamCount,
  maxTeamSize,
  readyTime,
  phase,
  bracketType,
  noDivider = false,
}: EventProps) => {
  const theme = useTheme();

  const prizeFormatted = usePrizeFormat(prize);
  const titleFormatted = title.replace(prizeFormatted, '').trim();

  // const viewerCountFormatted = useNumberFormat({
  //   notation: 'compact',
  //   maximumSignificantDigits: 3,
  // }).format(viewerCount);

  // TODO: this is correct but we do not have this routing implemented yet
  // const tournamentUrl = `/game/${toKebabCase(gameTitle)}/tournaments/${id}`;
  // const registerUrl = `${tournamentUrl}/register`;

  const registerUrl = useMemo(() => {
    return `/game/${toKebabCase(gameTitle)}/tournaments?tournamentId=${id}`;
  }, [gameTitle, id]);

  // TODO: Create new field on tournament 'teamUsers' and corresponding cloud function
  const router = useRouter();
  return (
    <>
      <ListItem
        onClick={() => {
          return router.push(registerUrl);
        }}
        sx={{
          display: 'flex',
          cursor: !!registerUrl ? 'pointer' : 'default',
          py: 1.5,
          pl: 1.5,
          pr: 0,
          background:
            phase === 'live'
              ? theme.palette.background.elevation['16']
              : theme.palette.background.elevation['10'],
        }}
      >
        <ListItemAvatar>
          <ImageOverlayed
            src={imgUrl}
            width={124}
            height={124}
            sx={{ border: '1px solid rgba(255, 255, 255, 0.6)' }}
          >
            <Typography
              variant="body2"
              align="center"
              color="text.primary"
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'bold',
                textShadow: '0px 0px 10px black',
              }}
            >
              {titleFormatted}
            </Typography>
          </ImageOverlayed>
        </ListItemAvatar>
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <Grid item sx={SX_ROW_GROUP} xs={12}>
            <Typography
              variant="h6"
              color={theme.palette.text.primary}
              sx={{
                textTransform: 'uppercase',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                mr: 1,
              }}
            >
              {titleFormatted}
            </Typography>
            {/* {finals && (
                <Chip
                  size="small"
                  sx={{ ml: 2, mt: 0.5, color: theme.palette.text.secondary }}
                  variant="gradientOutlinedHorizontal"
                  label={finals}
                />
              )} */}
            <ParticipantsWithIcon
              current={competitorCount}
              max={maxTeamSize * maxTeamCount}
            />
          </Grid>
          <Grid item sx={SX_ROW_GROUP} xs={12}>
            <Box sx={{ pb: 2.5 }}>
              <Typography
                variant="body1"
                color={theme.palette.text.secondary}
                // sx={{
                //   textTransform:
                //     gameTitle === 'ev.io' ? undefined : 'capitalize',
                // }}
              >
                {gameTitle}
              </Typography>

              {!!sponsors.length && sponsors[0].name && (
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}
                  >
                    {phase !== 'live'
                      ? `sponsored by
                   
                  ${sponsors && sponsors[0].name}`
                      : `${bracketType}`}
                  </Typography>
                </Box>
              )}
            </Box>
            {sponsors && (
              <RegisterButton
                phase={phase}
                sponsors={sponsors}
                registerUrl={registerUrl}
                isRegistered={isRegistered}
                isCheckedIn={isCheckedIn}
              />
            )}
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              ...SX_ROW_GROUP,
              mx: 0,
              background:
                phase === 'live'
                  ? 'linear-gradient(145deg, rgba(2, 37, 74, 0.8) 50%, rgba(4, 44, 84, 0.8) 50%)'
                  : 'linear-gradient(145deg, rgba(5, 49, 89, 0.8) 50%, rgba(11, 65, 110, 0.8) 50%)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                pl: 4,
              }}
            >
              <GradientIcon
                IconComponent={EmojiEventsIcon}
                gradientColor="secondary.horizontal"
              />
              <GradientTypography
                variant="subtitle1"
                gradientColor="secondary.vertical"
                sx={{ mx: 1 }}
              >
                {`${prizeFormatted} Prize Pool`}
              </GradientTypography>
            </Box>
            <TimeStatus
              phase={phase}
              date={date}
              endDate={endDate}
              readyTime={readyTime}
              liveUrl={registerUrl}
            />
          </Grid>
        </Grid>
      </ListItem>
      {!noDivider ? (
        <Divider
          sx={{ boxShadow: 1 }}
          orientation="horizontal"
          component="li"
        />
      ) : (
        ''
      )}
    </>
  );
};
